// Centered modal for destructive (or high-stakes primary) actions.
// Pairs with <DestructiveButton> for any irreversible flow per
// UI_GUIDELINES.md §3.2.
//
// Controlled component — caller owns `open`. On `onCancel` the caller
// flips open to false; the dialog itself doesn't unmount on cancel.
//
// Accessibility:
//   - role="dialog" + aria-modal="true" + aria-labelledby (title) +
//     aria-describedby (body)
//   - first focusable inside the dialog gets focus on open
//   - Tab cycles within the dialog (focus trap) so background
//     controls can't be reached via keyboard
//   - ESC fires onCancel (standard modal dismiss)
//   - Backdrop click fires onCancel (convention)
//   - Body scroll is locked while open so the backdrop doesn't
//     double-scroll the page underneath
//
// Visuals:
//   - Backdrop: black/40 semi-opaque
//   - Dialog: centered, max-w-md, white rounded card, gray border
//   - Title: text-lg semibold
//   - Body: text-sm gray-600
//   - Buttons: right-aligned (cancel outlined, confirm filled);
//     confirmVariant chooses PrimaryButton vs DestructiveButton
//
// Type-to-confirm:
//   - When `confirmInput` is a non-empty string, an input appears
//     below the body and the confirm button stays disabled until
//     the user types that exact string. Used for highest-stakes
//     actions ("Type the organization name to confirm").

import { useEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import PrimaryButton from './PrimaryButton.jsx';
import DestructiveButton from './DestructiveButton.jsx';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'destructive',
  confirmInput = null, // when set, user must type this to enable confirm
  loading = false,
  onConfirm,
  onCancel,
}) {
  const cardRef = useRef(null);
  const previouslyFocused = useRef(null);
  const titleId = useId();
  const bodyId = useId();
  const [typed, setTyped] = useState('');

  // Reset the type-to-confirm input every time the dialog opens.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  // Lock body scroll + manage focus while open.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Defer focus until after paint so the focused element exists.
    const id = window.setTimeout(() => {
      const first = cardRef.current?.querySelector(FOCUSABLE);
      if (first instanceof HTMLElement) first.focus();
    }, 0);

    return () => {
      window.clearTimeout(id);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the element that had it before the dialog opened.
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [open]);

  // ESC to cancel + Tab focus trap.
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!loading) onCancel?.();
        return;
      }
      if (e.key !== 'Tab' || !cardRef.current) return;
      const focusables = Array.from(cardRef.current.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const ConfirmButton = confirmVariant === 'primary' ? PrimaryButton : DestructiveButton;
  const typeToConfirmEnabled = confirmInput ? typed === confirmInput : true;

  function handleBackdropClick() {
    if (!loading) onCancel?.();
  }

  function handleCardClick(e) {
    e.stopPropagation();
  }

  const dialog = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onClick={handleCardClick}
        className="w-full max-w-md rounded-lg bg-white shadow-xl border border-gray-200"
      >
        <div className="px-6 pt-6 pb-4">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900">
            {title}
          </h2>
          {body && (
            <div id={bodyId} className="mt-2 text-sm text-gray-600">
              {body}
            </div>
          )}
          {confirmInput && (
            <div className="mt-4">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Type <span className="font-mono text-gray-900">{confirmInput}</span> to confirm
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                disabled={loading}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 rounded-b-lg bg-gray-50 px-6 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {cancelLabel}
          </button>
          <ConfirmButton
            onClick={onConfirm}
            disabled={!typeToConfirmEnabled}
            loading={loading}
          >
            {confirmLabel}
          </ConfirmButton>
        </div>
      </div>
    </div>
  );

  // Portal to document.body so the dialog's stacking isn't affected
  // by ancestor z-index or overflow rules. In SSR / vitest-without-
  // jsdom, `document` is undefined — render inline instead. The portal
  // is only load-bearing for z-index hoisting in a real browser.
  if (typeof document === 'undefined') return dialog;
  return createPortal(dialog, document.body);
}
